import type {UseMutationOptions, UseQueryOptions} from '@tanstack/react-query';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';

import {QUERY_CLIENT_MAX_AGE} from '#queries/constants.js';
import type {Nullable} from '#types/general.js';
import {buildPresenterURL, fetcher, RequestMethod} from '#utils/http.js';

const TEAMS_ENDPOINT = '/api/v3/teams';
const CURRENT_TEAM_ENDPOINT = '/api/v2/teams/current';
const TEAMS_ITEMS_PER_PAGE = 100;

const TEAM_FIELDS = {
  teams: ['id', 'name', 'require_admin_approval'],
  organization: ['id', 'name'],
};

export type Team = {
  id: number;
  name: string;
  organization_id: number;
  organization_name: string;
  require_admin_approval: boolean;
};

type TeamApiResponse = {
  id: number;
  name: string;
  organization_id: number;
  organization_name: string;
  require_admin_approval: boolean;
};

type TeamsApiResponse = {
  data: TeamApiResponse[];
  meta?: {
    total: number;
    page: number;
    pages: number;
    items: number;
  };
};

type TeamsResponse = Team[];

export type TeamsSearchParams = {
  id?: number;
  organizationId?: number;
};

type SwitchTeamRequest = {
  id: number;
};

type SwitchTeamResponse = {
  message?: string;
  team_id: number;
};

type TeamsQueryOptions = Omit<
  UseQueryOptions<TeamsResponse>,
  'queryKey' | 'queryFn'
>;

type TeamQueryOptions = Omit<
  UseQueryOptions<Team | null>,
  'queryKey' | 'queryFn'
>;

type SwitchTeamMutationOptions = Omit<
  UseMutationOptions<SwitchTeamResponse, Error, SwitchTeamRequest>,
  'mutationFn'
>;

export const teamQueryKeys = {
  all: ['teams'] as const,
  lists: () => [...teamQueryKeys.all, 'list'] as const,
  list: (search: TeamsSearchParams = {}) =>
    [...teamQueryKeys.lists(), search] as const,
  allPagesList: (search: TeamsSearchParams = {}) =>
    [...teamQueryKeys.list(search), 'all-pages'] as const,
  detail: (id: number) => [...teamQueryKeys.all, 'detail', id] as const,
} as const;

function buildTeamsSearchQuery(
  search?: TeamsSearchParams
): Record<string, number> {
  const params: Record<string, number> = {};

  if (search?.id !== undefined) {
    params['search[id_eq]'] = search.id;
  }

  if (search?.organizationId !== undefined) {
    params['search[organization_id_eq]'] = search.organizationId;
  }

  return params;
}

export async function fetchTeamsPage(
  signal: AbortSignal | undefined,
  page: number,
  search?: TeamsSearchParams
): Promise<TeamsApiResponse> {
  const url = buildPresenterURL(
    TEAMS_ENDPOINT,
    {
      fields: TEAM_FIELDS,
    },
    {page, items: TEAMS_ITEMS_PER_PAGE, ...buildTeamsSearchQuery(search)}
  );

  return fetcher<TeamsApiResponse>(url, {
    method: RequestMethod.GET,
    signal,
  });
}

export async function fetchTeams(signal?: AbortSignal): Promise<TeamsResponse> {
  const url = buildPresenterURL(
    TEAMS_ENDPOINT,
    {fields: TEAM_FIELDS},
    {items: TEAMS_ITEMS_PER_PAGE}
  );

  const response = await fetcher<TeamsApiResponse>(url, {
    method: RequestMethod.GET,
    signal,
  });

  return response.data;
}

export async function fetchTeam(
  id: number,
  signal?: AbortSignal
): Promise<Nullable<Team>> {
  const response = await fetchTeamsPage(signal, 1, {id});
  return response.data[0] ?? null;
}

export async function fetchAllTeams(
  signal?: AbortSignal,
  search?: TeamsSearchParams
): Promise<TeamsResponse> {
  const aggregated: Team[] = [];
  let page = 1;
  let pages = 1;

  do {
    const response = await fetchTeamsPage(signal, page, search);
    aggregated.push(...response.data);
    pages = response.meta?.pages ?? 1;
    page += 1;
  } while (page <= pages);

  return aggregated;
}

export async function switchTeam(
  request: SwitchTeamRequest
): Promise<SwitchTeamResponse> {
  return fetcher<SwitchTeamResponse>(CURRENT_TEAM_ENDPOINT, {
    method: RequestMethod.POST,
    body: request,
  });
}

export function teamsQueryOptions(
  options?: TeamsQueryOptions
): UseQueryOptions<TeamsResponse> {
  return {
    queryKey: teamQueryKeys.list(),
    queryFn: ({signal}) => fetchTeams(signal),
    staleTime: QUERY_CLIENT_MAX_AGE / 24,
    gcTime: QUERY_CLIENT_MAX_AGE,
    refetchOnWindowFocus: false,
    ...options,
  };
}

export function teamQueryOptions(
  id: number,
  options?: TeamQueryOptions
): UseQueryOptions<Team | null> {
  return {
    queryKey: teamQueryKeys.detail(id),
    queryFn: ({signal}) => fetchTeam(id, signal),
    staleTime: QUERY_CLIENT_MAX_AGE / 24,
    gcTime: QUERY_CLIENT_MAX_AGE,
    refetchOnWindowFocus: false,
    ...options,
  };
}

function allPagesTeamsQueryOptions(
  search?: TeamsSearchParams,
  options?: TeamsQueryOptions
): UseQueryOptions<TeamsResponse> {
  return {
    queryKey: teamQueryKeys.allPagesList(search ?? {}),
    queryFn: ({signal}) => fetchAllTeams(signal, search),
    staleTime: QUERY_CLIENT_MAX_AGE / 24,
    gcTime: QUERY_CLIENT_MAX_AGE,
    refetchOnWindowFocus: false,
    ...options,
  };
}

export function allTeamsQueryOptions(
  options?: TeamsQueryOptions
): UseQueryOptions<TeamsResponse> {
  return allPagesTeamsQueryOptions(undefined, options);
}

export function organizationTeamsQueryOptions(
  organizationId: number,
  options?: TeamsQueryOptions
): UseQueryOptions<TeamsResponse> {
  return allPagesTeamsQueryOptions({organizationId}, options);
}

export function useTeams(options?: TeamsQueryOptions) {
  return useQuery(teamsQueryOptions(options));
}

export function useTeam(id: number | undefined, options?: TeamQueryOptions) {
  return useQuery({
    ...teamQueryOptions(id ?? 0),
    ...options,
    enabled: id !== undefined && (options?.enabled ?? true),
  });
}

// TEMPORARY: `fetchAllTeams` grabs every paginated page client side. Remove useAllTeams /
// allTeamsQueryOptions once the audiences create-button flow no
// longer needs the full org team list fetched this way.
export function useAllTeams(options?: TeamsQueryOptions) {
  return useQuery(allTeamsQueryOptions(options));
}

export function useOrganizationTeams(
  organizationId: number | undefined,
  options?: TeamsQueryOptions
) {
  return useQuery({
    ...organizationTeamsQueryOptions(organizationId ?? 0),
    ...options,
    enabled: organizationId !== undefined && (options?.enabled ?? true),
  });
}

export function useSwitchTeam(options?: SwitchTeamMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: switchTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: teamQueryKeys.all});
    },
    ...options,
  });
}

export function useInvalidateTeams() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({queryKey: teamQueryKeys.all}),
    [queryClient]
  );
}
